import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/cloud-arrow-up.svg';
import { ReactComponent as InputErrorIcon } from 'assets/icons/input_error.svg';
import PDF from 'assets/icons/pdf.svg';

import { standaloneToast } from 'utils';

import { SERVICE_CARD_BORDER_RADIUS } from '../ServiceFormCard';

type InputPropTypes = {
  error?: string;
  invalid?: boolean;
  isRequired?: boolean;
  label: string;
  // eslint-disable-next-line no-unused-vars
  getValue: (value: string) => void;
  selectedFile: Array<ThumbFileType>;
  // eslint-disable-next-line no-unused-vars
  setSelectedFile: (data: Array<ThumbFileType>) => void;
};
type ThumbFileType = File & { preview: string };
const Uploader: React.FC<InputPropTypes> = ({
  invalid,
  isRequired = false,
  error,
  selectedFile,
  setSelectedFile,
  label,
  getValue,
}) => {
  const getBase64 = async (file: any) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    multiple: false,
    onDrop: async (acceptedFiles) => {
      fileRejections.forEach((file) => {
        file.errors.forEach((err) => {
          if (err.code === 'file-too-large') {
            standaloneToast({
              position: 'top-right',
              description: 'File is too large (max size : 3MB)',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }

          if (err.code === 'file-invalid-type') {
            standaloneToast({
              position: 'top-right',
              description: 'Invalid file (just: PNG, JPG, JPEG, PDF)',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }
        });
      });
      setSelectedFile(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );

      const uploadedFile = acceptedFiles[0];
      const res = await getBase64(uploadedFile);
      getValue(res || '');
    },
    maxFiles: 1,
    accept: {
      'image/png': ['.png', '.jpeg', '.jpg'],
      'application/pdf': ['.pdf'],
    },
    maxSize: 3000000,
  });
  const thumbs = selectedFile.map((file: ThumbFileType) => {
    const type = file.type.split('/')[0];
    return (
      <HStack
        key={file.name}
        gap={3}
        align="center"
        justifyContent="space-between"
        w="full"
        zIndex={1}
        minW={0}
      >
        <HStack gap={3} align="center" minW={0} flex="1">
          <Box
            flexShrink={0}
            w="48px"
            h="48px"
            borderRadius="md"
            overflow="hidden"
            bg="rgba(255,255,255,0.06)"
          >
            <img
              src={type === 'application' ? PDF : file.preview}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onLoad={() => {
                URL.revokeObjectURL(file.preview);
              }}
            />
          </Box>
          <Text
            fontSize="sm"
            fontWeight="medium"
            color="body"
            flex="1"
            minW={0}
            lineHeight="short"
            truncate
            title={file.name}
          >
            {file.name}
          </Text>
        </HStack>
        <Box
          as="button"
          type="button"
          flexShrink={0}
          lineHeight={0}
          cursor="pointer"
          p={1}
          borderRadius="md"
          aria-label="Remove file"
          _hover={{ bg: 'rgba(255,255,255,0.08)' }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setSelectedFile([]);
            getValue('');
          }}
        >
          <CloseIcon />
        </Box>
      </HStack>
    );
  });
  return (
    <VStack align="flex-start" pos="relative">
      {label ? <Text fontSize="sm">{label}</Text> : null}
      <Stack
        w="full"
        borderRadius={SERVICE_CARD_BORDER_RADIUS}
        position="relative"
        overflow="hidden"
        direction="row"
        align="center"
        justify="center"
        px="6"
        py="5"
        minH={{ base: '100px', md: '108px' }}
        cursor="pointer"
        css={{
          '& :before': {
            content: '" "',
            position: 'absolute',
            border: '10px dashed ',
            borderColor: selectedFile.length > 0 ? 'primary' : '#757575',
            top: '-8px',
            bottom: '-8px',
            left: '-8px',
            right: '-8px',
            zIndex: 0,
          },
        }}
        {...getRootProps()}
      >
        <input
          {...getInputProps()}
          required={isRequired}
          aria-invalid={Boolean(invalid)}
          style={{ display: 'none' }}
        />
        {selectedFile.length > 0 ? (
          thumbs
        ) : (
          <Stack
            zIndex={1}
            direction={{ base: 'column', sm: 'row' }}
            gap={{ base: 3, sm: 2 }}
            align="center"
            textAlign={{ base: 'center', sm: 'left' }}
          >
            <Box flexShrink={0} lineHeight={0}>
              <UploadIcon />
            </Box>
            <HStack gap={1} flexWrap="wrap" justify="center">
              <Text
                fontSize="xs"
                fontWeight="medium"
                whiteSpace={{ sm: 'nowrap' }}
              >
                Drop your file here, or
              </Text>
              <Text color="primary" fontSize="xs" fontWeight="medium">
                Browse
              </Text>
            </HStack>
          </Stack>
        )}
      </Stack>
      <Text mt="1" fontSize="xs">
        JPEG, JPG, PNG, PDF (MAX 3MB)
      </Text>
      {error && (
        <Box
          color="error"
          fontSize="xs"
          textAlign="start"
          display="flex"
          alignItems="center"
          gap={1}
          pos="absolute"
          left={0}
          bottom={-5}
        >
          <InputErrorIcon />
          {error}
        </Box>
      )}
    </VStack>
  );
};
export default Uploader;
